import {defineComponent, ref, h, onMounted} from 'vue';
import {useIntersectionObserver} from './useIntersectionObserver';

interface UseInfiniteScrollOptions {
    /** IntersectionObserver options */
    options?: IntersectionObserverInit;
    /**
     * Start observing at mount
     * @default true
     */
    immediate?: boolean;
}

/**
 * Composable to add infinite scroll behaviour to a custom html element
 * It returns a custom component that serve as a intersection target (but any element can be used)
 * It also returns a reactive ref indicating if the element is intersecting
 *
 * @param callback - Callback called when the element is observed
 * @param options - composable options {@link UseInfiniteScrollOptions}
 *
 * @usages
 *
 * ```vue
 *  <template>
 *      <div class='my-long-list'>
 *         <div v-for='element of myPaginationElements'>...</div>
 *          <InfiniteScrollWitness ref='witnessRef'/>
 * <--- Or with a custom spinner --->
 *          <InfiniteScrollWitness ref='witnessRef'>
 *            <VJoySpinner/>
 *          </InfiniteScrollWitness/>
 *      </div>
 *  </template>
 *
 * <script setup lang="ts">
 *   const {InfiniteScrollWitness, witnessRef, isIntersecting} = useInfiniteScroll((entries) => {
        if (isIntersecting.value) {
            fetchMoreElements()
        }
    });
 * </script>
 * ```
 */
export function useInfiniteScroll(callback?: IntersectionObserverCallback, options?: UseInfiniteScrollOptions) {
    /** Default "witness" component that will be observed for intersection */
    const InfiniteScrollWitness = defineComponent({
        name: 'InfiniteScrollWitness',
        emits: ['mount'],
        setup(_, {slots, emit}) {
            onMounted(() => emit('mount'));
            return () =>
                h(
                    'div',
                    {
                        class: 'intersection-witness',
                        style: {width: '100%', minHeight: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center'},
                    },
                    [slots.default?.()],
                );
        },
    });

    const witnessRef = ref<InstanceType<typeof InfiniteScrollWitness> | undefined | null>();

    const {startObserving, stopObserving, isIntersecting} = useIntersectionObserver(witnessRef, callback, options);

    return {
        InfiniteScrollWitness,
        witnessRef,
        isIntersecting,
        startObserving,
        stopObserving,
    };
}
